/**
 * API 加密工具函数（符合 Blockwatch API 接口安全增强规范）
 * 使用 RSA + AES-GCM 混合加密
 *
 * 加密方案：
 * - RSA-OAEP-SHA256：加密 AES 密钥
 * - AES-128-GCM：加密数据主体
 *
 * 使用 node-forge 库，不受 HTTPS 限制
 *
 * 参考：src/API加密前端实现指南.md
 */

import forge from "node-forge";

// RSA 测试公钥（PEM 格式）
// const RSA_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
// MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA42m8cdj89936CO5I5hWB
// D/ri3j18ghLGdSLswz3i7n949TMUToYlJEyOTs1t/4Vt6rxEgvY3AdL5ouVWQxfW
// MZfrGKdNNR1v0DxIXfzF8UcE01PiHXY67Kw9uaQL7AgGX3UL7FGDwRomKajtSyHB
// 88IJrb+tld50ciFYJkXlhYhtchVDlAb+UlxU/wqLBmtWz8gSMABrxA/PcE0zE7Gp
// eRiW9slpm317jmF5jpvmZMOHE3fF6lJBV5SrfXDgEF3aoIQG26wPPSRq2PC93Gmx
// 6DQwDGnZ3BXk40/IeitH/tjaiAw4GxEukkhOzVbu6U8QIwxNUi64XlzoM04xGyru
// aQIDAQAB
// -----END PUBLIC KEY-----`;

// 正式 公钥
const RSA_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAtr9p01YxViX8SdOo4KyG
5Q7fryP1NcxOP34Ftiqt7c71uqc74bSQl4iZt8RTslEwPzVX6Dbn+n3aKk8cz+SI
bsq6JE53qbQvDepRmpDZgDbK95XVv79UysDKNDpXGcwFsTdAWG27dMZmhIY9v1ru
xnNfOpepkiD3KIkyooYl6q92fTtQKyKjGFBC2XZ8G5FX90CAhzRuIO4nEcSrF/h/
fU5bYsJiiVaWMN0Rxx4jNvrUP1R5OJi710+CK3RN9l+FRKUjLU562NEcAc/XEoi/
eV2HrcYHxcHtP2ZflB9TyM8gtprmA3QNtAS+DSIzxhKdYMu0XqzJyZ5YMiKkWjrA
gwIDAQAB
-----END PUBLIC KEY-----`;

/**
 * 将 Uint8Array 转换为 Base64 字符串
 */
function uint8ArrayToBase64(uint8Array) {
  let binary = "";
  for (let i = 0; i < uint8Array.length; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return window.btoa(binary);
}

/**
 * 将 Base64 字符串转换为 Uint8Array
 */
function base64ToUint8Array(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * 从 PEM 格式导入 RSA 公钥
 */
function importRSAPublicKey() {
  return forge.pki.publicKeyFromPem(RSA_PUBLIC_KEY);
}

/**
 * 用 RSA-OAEP-SHA256 加密 AES 密钥（使用 node-forge）
 */
function encryptAesKeyWithRsaForge(aesKey) {
  try {
    const publicKey = importRSAPublicKey();

    // 将 Uint8Array 转换为 forge bytes
    const forgeBytes = forge.util.createBuffer(aesKey);

    // 使用 RSA-OAEP-SHA256 加密
    const encryptedKey = publicKey.encrypt(forgeBytes.getBytes(), "RSA-OAEP", {
      md: forge.md.sha256.create(),
      mgf1: {
        md: forge.md.sha256.create(),
      },
    });

    return forge.util.encode64(encryptedKey);
  } catch (error) {
    console.error("RSA 加密失败 (node-forge):", error);
    throw new Error(`RSA 加密失败: ${error.message}`);
  }
}

/**
 * 用 AES-GCM 加密数据（请求加密）
 * 返回格式：Base64 编码的 ciphertext + authTag（不含 nonce）
 *
 * 根据 API 加密前端实现指南：
 * - 请求体只包含 ciphertext + authTag
 * - nonce 通过 X-Nonce 头传输
 */
function encryptWithAesGcm(aesKey, data, nonce) {
  try {
    const plaintext = JSON.stringify(data);
    const utf8Bytes = new TextEncoder().encode(plaintext); // 确保 UTF-8
    // 将 nonce 和 aesKey 转换为 forge bytes
    const forgeAesKey = forge.util.createBuffer(aesKey);
    const forgeNonce = forge.util.createBuffer(nonce);

    // 创建 AES-GCM cipher
    const cipher = forge.cipher.createCipher("AES-GCM", forgeAesKey.getBytes());

    // 设置 IV (nonce)
    cipher.start({
      iv: forgeNonce.getBytes(),
    });

    // 加密数据
    cipher.update(forge.util.createBuffer(utf8Bytes), "utf8");
    cipher.finish();

    // 获取加密结果和 authTag
    const encrypted = cipher.output.getBytes();
    const authTag = cipher.mode.tag.getBytes();

    // 拼接 ciphertext + authTag（不含 nonce）
    const combined = encrypted + authTag;

    return forge.util.encode64(combined);
  } catch (error) {
    console.error("AES-GCM 加密失败:", error);
    throw new Error(`AES-GCM 加密失败: ${error.message}`);
  }
}

/**
 * 用 AES-GCM 解密数据（响应解密）
 * 输入格式：Base64 编码的 ciphertext(N) + authTag(16)
 * nonce 从响应头 X-Nonce 获取
 *
 * 根据 API 加密前端实现指南：
 * 1. 从响应头 X-Nonce 获取服务端 Nonce (Base64)
 * 2. 将响应体解析为二进制 Buffer
 * 3. 解析格式: ciphertext(N) + authTag(16字节)
 * 4. 用 AES-128-GCM 解密（使用请求时生成的 AES 密钥）
 * 5. 将解密结果转为 JSON 对象
 */
function decryptWithAesGcm(aesKey, nonceBase64, encryptedBodyBase64) {
  try {
    // 将 Base64 nonce 转为二进制字符串
    const responseNonce = window.atob(nonceBase64);

    // 解码响应体 Base64
    let binaryString = window.atob(encryptedBodyBase64);

    // 响应体格式: ciphertext(N) + tag(16)（不含 nonce）
    if (binaryString.length < 16) {
      throw new Error("响应体太短");
    }

    const ciphertext = binaryString.substring(0, binaryString.length - 16);
    const authTag = binaryString.substring(binaryString.length - 16);
    // 将 aesKey Uint8Array 转换为二进制字符串
    let aesKeyBytes = "";
    for (let i = 0; i < aesKey.length; i++) {
      aesKeyBytes += String.fromCharCode(aesKey[i]);
    }

    // AES-128-GCM 解密
    const decipher = forge.cipher.createDecipher("AES-GCM", aesKeyBytes);
    decipher.start({
      iv: responseNonce, // 使用响应头的 X-Nonce
      tagLength: 128,
      tag: forge.util.createBuffer(authTag),
    });
    decipher.update(forge.util.createBuffer(ciphertext));
    const success = decipher.finish();

    if (!success) {
      throw new Error("解密失败：认证标签不匹配");
    }

    // Forge 的 getBytes() 返回的是字节字符串
    // 每个字节对应一个字符（0x00-0xFF）
    // 需要先转为 Uint8Array，再用 TextDecoder 解码 UTF-8
    const plaintextBytes = decipher.output.getBytes();

    // 将字节字符串转为 Uint8Array
    const uint8Array = new Uint8Array(plaintextBytes.length);
    for (let i = 0; i < plaintextBytes.length; i++) {
      uint8Array[i] = plaintextBytes.charCodeAt(i);
    }

    // 使用 TextDecoder 解码 UTF-8
    const plaintext = new TextDecoder("utf-8").decode(uint8Array);

    const result = JSON.parse(plaintext);
    return result;
  } catch (error) {
    throw new Error(`AES-GCM 解密失败: ${error.message}`);
  }
}

/**
 * 加密请求数据（符合 API 加密前端实现指南）
 * @param {Object} data - 要加密的数据
 * @returns {Promise<Object>} 包含加密数据和密钥的对象
 */
export async function encryptRequest(data) {
  try {
    // 1. 生成 16 字节随机 AES 密钥
    const aesKey = window.crypto.getRandomValues(new Uint8Array(16));

    // 2. 生成 12 字节随机 Nonce
    const nonce = window.crypto.getRandomValues(new Uint8Array(12));

    // 3. 生成秒级时间戳（10位数字）
    const timestamp = Math.floor(Date.now() / 1000);

    // 4. RSA-OAEP-SHA256 加密 AES 密钥
    const encryptedKey = encryptAesKeyWithRsaForge(aesKey);

    // 5. AES-128-GCM 加密数据（请求体不包含 nonce）
    const encryptedBody = encryptWithAesGcm(aesKey, data, nonce);

    return {
      encryptedKey: encryptedKey,
      nonce: uint8ArrayToBase64(nonce),
      timestamp: timestamp, // 秒级时间戳
      body: encryptedBody,
      aesKey: aesKey,
    };
  } catch (error) {
    throw error;
  }
}

/**
 * 解密响应数据（符合 API 加密前端实现指南）
 * @param {ArrayBuffer|Uint8Array|string} encryptedBody - 加密的响应体（二进制或 Base64）
 * @param {Uint8Array} aesKey - AES 密钥
 * @param {string} nonceBase64 - 从响应头 X-Nonce 获取的服务端 nonce (Base64)
 * @returns {Promise<Object>} 解密后的数据
 */
export async function decryptResponse(encryptedBody, aesKey, nonceBase64) {
  try {
    // 转换为 Base64 字符串
    let encryptedBodyBase64;
    if (encryptedBody instanceof ArrayBuffer) {
      const uint8Array = new Uint8Array(encryptedBody);
      encryptedBodyBase64 = uint8ArrayToBase64(uint8Array);
    } else if (encryptedBody instanceof Uint8Array) {
      encryptedBodyBase64 = uint8ArrayToBase64(encryptedBody);
    } else if (typeof encryptedBody === "string") {
      // 如果已经是 Base64 字符串，直接使用
      encryptedBodyBase64 = encryptedBody;
    } else {
      throw new Error("不支持的加密数据类型");
    }

    // 响应体格式: ciphertext(N) + authTag(16)，nonce 从响应头获取
    return await decryptWithAesGcm(aesKey, nonceBase64, encryptedBodyBase64);
  } catch (error) {
    throw new Error("解密响应失败: " + error.message);
  }
}

/**
 * 检查时间戳是否有效（300秒内）
 * @param {number} timestamp - 秒级时间戳
 * @param {number} maxAge - 最大有效时间（秒），默认 300 秒
 */
export function isTimestampValid(timestamp, maxAge = 300) {
  const now = Math.floor(Date.now() / 1000);
  const diff = Math.abs(now - timestamp);
  return diff <= maxAge;
}
